export interface IEnvironmentConfig {
    mcTupReconfigUrl: string;
    idpUrl: string;
    clientId: string;
    env: Environment;
    instrumentationKey?: string;
}

export interface IEnvironmentConfigs {
    [envName: string]: IEnvironmentConfig;
}

export enum Environment {
    PROD = 'PROD',
    STG = 'STG',
    DEV = 'DEV',
    LOCAL = 'LOCAL',
    SBX = 'SBX'
}

// Direct mapping from hostname to environment
// Each hostname is explicitly assigned to an environment for clarity
const hostnameToEnvironment: Record<string, string> = {
    // Local development
    'localhost': 'local',
    '127.0.0.1': 'local',

    // Development environments
    'supplychain-dev.samsclub.com': 'dev',
    'dev-sams-tms-sc-visibility.cld.samsclub.com': 'dev',

    // Staging environments
    'supplychain-stg.samsclub.com': 'stg',
    'stg-supplychain-e2e-visibility.cld.samsclub.com': 'stg',
    'supplychain-stgx.samsclub.com': 'stg',
    'supplychain-sbx.samsclub.com': 'stg',
    'supplychain-vaas.samsclub.com': 'stg',
    'supplychain-sbx01.samsclub.com': 'stg',

    // Production environments
    'supplychain-beta.samsclub.com': 'prod',
    'supplychain.samsclub.com': 'prod',
    'vision.cld.samsclub.com': 'prod',
    'prod.vision-gateway.samsclub.com': 'prod',
    'supplychain-beta1.samsclub.com': 'prod', // Explicitly mapped to prod
    'vision-gateway-beta.sams-sc-vision.wus-prod-a92.cluster.k8s.us.walmart.net': 'prod'
};

// Configuration settings for each environment
export const environmentConfigs: IEnvironmentConfigs = {
    local: {
        idpUrl: 'https://retaillink.login.stg.wal-mart.com', // Use staging IDP for local
        mcTupReconfigUrl: 'https://dev.missioncontrol.walmart.com/fcInbound/tup',
        clientId: '5b5d5deb-958b-4bc0-a246-83ab19b7381f',
        env: Environment.LOCAL
    },
    dev: {
        idpUrl: 'https://retaillink.login.stg.wal-mart.com',
        mcTupReconfigUrl: 'https://dev.missioncontrol.walmart.com/fcInbound/tup',
        clientId: '5b5d5deb-958b-4bc0-a246-83ab19b7381f',
        env: Environment.DEV
    },
    stg: {
        idpUrl: 'https://retaillink.login.stg.wal-mart.com',
        mcTupReconfigUrl: 'https://stg.missioncontrol.walmart.com/fcInbound/tup',
        clientId: '5b5d5deb-958b-4bc0-a246-83ab19b7381f',
        instrumentationKey: '7fa873fb-cbe5-4e03-9932-bf2b4230a2f4',
        env: Environment.STG
    },
    prod: {
        idpUrl: 'https://retaillink.login.wal-mart.com', // Production IDP URL
        mcTupReconfigUrl: 'https://missioncontrol.walmart.com/fcInbound/tup',
        clientId: '5b5d5deb-958b-4bc0-a246-83ab19b7381f',
        instrumentationKey: '7023ebd3-a602-4eef-8e94-3ed1ec5d8cbc',
        env: Environment.PROD
    },
};

const ORIGIN = window.location.origin;

// Extract just the hostname for comparison (no protocol, no port, no path)
const currentHostname = new URL(ORIGIN).hostname;

// Look up the environment directly from the hostname map or fall back to dev
const currentEnvironment = hostnameToEnvironment[currentHostname] || 'dev';

// For debugging - remove these lines once confirmed working
console.debug('Current hostname:', currentHostname);
console.debug('Detected environment:', currentEnvironment);

export const selectedEnvConfig = environmentConfigs[currentEnvironment];

// API URL getters
export const getLocalBaseURL = () => ORIGIN;
export const getOccBaseURL = () => `${ORIGIN}/api/ob-communication`;
export const getFcIbBaseURL = () => `${ORIGIN}/api/fc-inbound`;
export const getEventTrackerBaseURL = () => `${ORIGIN}/api/tracker`;
export const getMcTupReconfigUrl = () => selectedEnvConfig.mcTupReconfigUrl;
export const getIDPURL = () => selectedEnvConfig.idpUrl;
export const getAuthCallbackAPI = () => `${ORIGIN}/api/vision/auth/provider/oidc/falcon/callback`;
export const getVisionLogoutAPI = () => `${ORIGIN}/api/vision/auth/logout`;
export const getNavigationConfigURL = () => `${ORIGIN}/api/vision/navigation/config`;
export const getUserProfileURL = () => `${ORIGIN}/api/vision/auth/profile`;
export const getActiveSessionURL = () => `${ORIGIN}/api/vision/auth/session`;
export const getClientId = () => selectedEnvConfig.clientId;
export const getProdFlag = () => selectedEnvConfig.env === Environment.PROD;
export const getInstrumentationKey = () => selectedEnvConfig.instrumentationKey;